/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../modules/auth/useAuth";
import {
  Modal, Button, TextField, Select,
} from "@xfrontend/xui-react-plus";
import { toast } from "#/infra/Toastify";
import updateLicense from "#/apis/infra/updateLicense";
import { LicenseItem } from "#/apis/infra/listLicenses";

const StyledModal = styled(Modal)`
  .modal-dialog { width: 460px; max-width: calc(100% - 32px); }
  .modal-content { background: var(--x-bg-500, #2d293a); border: 1px solid var(--x-purple-500, #494358); }
`;

const Field = styled.div`
  margin-bottom: 14px;
  label { display: block; font-size: 12.5px; color: var(--x-gray-400, #cfd0d3); margin-bottom: 5px; }
`;

const Note = styled.p`
  font-size: 12px; color: var(--x-gray-500, #a9a9ac); margin: 0 0 14px;
`;

type Option = { value: string; label: string };

type OrgEditModalProps = {
  show: boolean;
  org: LicenseItem | null;
  onClose: () => void;
  onSaved: () => void;
};

const OrgEditModal = ({
  show, org, onClose, onSaved,
}: OrgEditModalProps): JSX.Element | null => {
  const { t } = useTranslation("management");
  const auth = useAuth();
  const [status, setStatus] = useState(org?.license_status ?? "enable");
  const [expiresAt, setExpiresAt] = useState((org?.expires_at ?? "").slice(0, 10));
  const [busy, setBusy] = useState(false);

  // The modal stays mounted between opens, so re-seed the fields from the current org each
  // time it is shown (initial useState values only apply on first mount).
  useEffect(() => {
    if (show && org) {
      setStatus(org.license_status ?? "enable");
      setExpiresAt((org.expires_at ?? "").slice(0, 10));
    }
  }, [show, org?.license_id, org?.license_status, org?.expires_at]);

  if (!org) return null;

  const statusOptions: Option[] = [
    { value: "enable", label: t("status.enabled") },
    { value: "disable", label: t("status.disabled") },
  ];

  const submit = (): void => {
    const token = auth.user?.access_token;
    if (!token || busy) return;
    setBusy(true);
    updateLicense({
      token,
      licenseId: org.license_id,
      body: { license_status: status, expires_at: expiresAt },
    }).then((res) => {
      setBusy(false);
      if (res.success) {
        toast.success(t("toast.orgUpdated"));
        onSaved();
        onClose();
      } else {
        toast.error(res.msg || t("toast.failed"));
      }
    });
  };

  return (
    <StyledModal
      title={`${t("orgEdit.title")} — ${org.license_name}`}
      show={show}
      onClose={onClose}
      showCloseBtn
      restoreFocus={false}
    >
      <Modal.Body>
        <Note>{t("orgEdit.note")}</Note>
        <Field>
          <label htmlFor="oe-status">{t("orgEdit.status")}</label>
          <Select
            name="oe-status"
            isSearchable={false}
            options={statusOptions}
            value={statusOptions.find((o) => o.value === status) ?? null}
            onChange={(selected: unknown) => setStatus((selected as Option | null)?.value ?? "enable")}
          />
        </Field>
        <Field>
          <label htmlFor="oe-exp">{t("orgEdit.expiresAt")}</label>
          <TextField
            id="oe-exp"
            value={expiresAt}
            placeholder="2027-01-01"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExpiresAt(e.target.value)}
          />
        </Field>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>{t("orgEdit.cancel")}</Button>
        <Button variant="primary" disabled={busy} onClick={submit}>{t("orgEdit.submit")}</Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default OrgEditModal;
